/**
 * AudioPlayerContext
 * 
 * Provides a shared audio player that persists across view changes.
 * Allows seamless audio playback when transitioning between map and action views.
 */

import React, { createContext, useContext, useRef, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface AudioPlayerContextType {
  // Audio element ref (for direct control if needed)
  audioRef: React.RefObject<HTMLAudioElement | null>;
  
  // Playback state
  isPlaying: boolean;
  audioError: boolean;
  currentTime: number;
  duration: number;
  
  // Current audio URL
  audioUrl: string | null;
  
  // Control functions
  play: () => Promise<void>;
  pause: () => void;
  toggle: () => void;
  setAudioUrl: (url: string | null) => void;
  seek: (time: number) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | null>(null);

interface AudioPlayerProviderProps {
  children: ReactNode;
}

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrlState] = useState<string | null>(null);
  const autoplayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();
  
  // Play audio
  const play = async () => {
    if (!audioRef.current) return;
    
    try {
      await audioRef.current.play();
      setIsPlaying(true);
      setAudioError(false);
    } catch (error) {
      console.error('Failed to play audio:', error);
      setAudioError(true);
      setIsPlaying(false);
    }
  };
  
  // Pause audio
  const pause = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setIsPlaying(false);
  };
  
  // Toggle play/pause
  const toggle = () => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  };
  
  // Seek to specific time
  const seek = (time: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };
  
  // Set audio URL (with smart handling to avoid restarting same audio)
  const setAudioUrl = (url: string | null) => {
    const timestamp = new Date().toISOString();
    const isExplorationAudio = url?.includes('narration/v4/') && !url?.includes('quest-narration');
    const isQuestAudio = url?.includes('quest-narration/v4/');
    
    console.log('═══════════════════════════════════════════════════════');
    console.log(`🔄 [INVESTIGATION] setAudioUrl called at ${timestamp}`);
    console.log('   Old URL:', audioUrl);
    console.log('   New URL:', url);
    console.log('   Audio Type:', isExplorationAudio ? 'EXPLORATION' : isQuestAudio ? 'QUEST' : 'UNKNOWN');
    console.log('   Call Stack:');
    console.trace();
    console.log('═══════════════════════════════════════════════════════');
    
    // Clear any pending autoplay
    if (autoplayTimeoutRef.current) {
      console.log('⏹️ [INVESTIGATION] Clearing pending autoplay timer');
      clearTimeout(autoplayTimeoutRef.current);
      autoplayTimeoutRef.current = null;
    }
    
    // If same URL, don't change anything (keep playing)
    if (url === audioUrl) {
      console.log('✅ [INVESTIGATION] Same URL, keeping current audio - NO CHANGE');
      return;
    }
    
    // Different URL - stop current and load new
    if (audioRef.current) {
      console.log('⏹️ [INVESTIGATION] Stopping current audio and resetting');
      console.log('   Stopping audio that was:', audioUrl);
      console.log('   Will load new audio:', url);
      pause();
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
    }
    
    console.log('📝 [INVESTIGATION] Setting new audio URL state:', url);
    setAudioUrlState(url);
    setAudioError(false);
    
    // Explicitly reload audio element with new src
    if (audioRef.current && url) {
      console.log('🔄 [INVESTIGATION] Explicitly loading new audio source');
      // The src will update via React, but we need to call load() to fetch it
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.load();
          console.log('📂 [INVESTIGATION] audio.load() called for:', url);
        }
      }, 0);
    }
    // Note: autoplay happens in the effect below that watches for audioUrl changes
  };
  
  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    console.log('🎧 [INVESTIGATION] Setting up audio element event listeners');
    
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => {
      console.log('⏱️ [INVESTIGATION] Duration changed:', audio.duration, 'src:', audio.src);
      setDuration(audio.duration);
    };
    const handleEnded = () => {
      console.log('🏁 [INVESTIGATION] Audio ended:', audio.src);
      setIsPlaying(false);
    };
    const handleError = () => {
      console.error('❌ [INVESTIGATION] Failed to load audio narration');
      console.error('   Audio src:', audio.src);
      console.error('   Error:', audio.error);
      setAudioError(true);
      setIsPlaying(false);
    };
    const handleCanPlay = () => {
      console.log('✅ [INVESTIGATION] Audio canplay event:', audio.src);
      console.log('   Duration:', audio.duration);
      console.log('   Ready state:', audio.readyState);
      setAudioError(false);
      setDuration(audio.duration);
    };
    const handleLoadStart = () => {
      console.log('🔄 [INVESTIGATION] Audio loadstart event:', audio.src);
    };
    const handleLoadedData = () => {
      console.log('📥 [INVESTIGATION] Audio loadeddata event:', audio.src);
      console.log('   Ready state:', audio.readyState);
    };
    const handlePlay = () => {
      console.log('▶️ [INVESTIGATION] Audio play event:', audio.src);
    };
    const handlePause = () => {
      console.log('⏸️ [INVESTIGATION] Audio pause event:', audio.src);
    };
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    
    return () => {
      console.log('🧹 [INVESTIGATION] Removing audio element event listeners');
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);
  
  // Auto-play new audio when audioUrl changes (with 2 second delay, matching original behavior)
  useEffect(() => {
    // AUDIO AUTOPLAY DISABLED FOR PERFORMANCE
    console.log('🔇 [AUDIO DISABLED] Autoplay disabled for performance');
    // eslint-disable-next-line no-useless-return
    return;
    
    /* DISABLED CODE BELOW - Kept for reference
    console.log('🎬 [INVESTIGATION] Autoplay useEffect triggered');
    console.log('   audioUrl:', audioUrl);
    console.log('   audioRef.current exists:', !!audioRef.current);
    console.log('   audioRef.current.src:', audioRef.current?.src);
    
    if (!audioUrl || !audioRef.current) {
      console.log('⏭️ [INVESTIGATION] Skipping autoplay - no URL or no ref');
      return;
    }
    
    const audio = audioRef.current;
    console.log('📊 [INVESTIGATION] Audio ready state:', audio.readyState);
    
    // Wait for audio to be ready, then wait 2 more seconds
    const handleCanPlay = () => {
      console.log('✅ [INVESTIGATION] Audio can play, setting 2s autoplay timer for:', audioUrl);
      
      // Store timeout in ref so it can be cancelled from setAudioUrl()
      autoplayTimeoutRef.current = setTimeout(() => {
        console.log('🎵 [INVESTIGATION] Autoplay timer fired! Playing:', audioRef.current?.src);
        console.log('   Expected URL:', audioUrl);
        console.log('   Actual src:', audioRef.current?.src);
        
        // Play audio directly here (don't call play() to avoid dependency loop)
        if (audioRef.current) {
          audioRef.current.play()
            .then(() => {
              console.log('✅ [INVESTIGATION] Audio playback started successfully');
              setIsPlaying(true);
            })
            .catch((error) => {
              console.warn('⚠️ [INVESTIGATION] Autoplay blocked or failed:', error);
              setIsPlaying(false);
            });
        }
      }, 2000); // 2 second delay
      
      console.log('⏰ [INVESTIGATION] Autoplay timeout ID set:', autoplayTimeoutRef.current);
    };
    
    // If already loaded, start delay immediately
    if (audio.readyState >= 2) {
      console.log('✅ [INVESTIGATION] Audio already loaded (readyState >= 2), starting autoplay timer immediately');
      handleCanPlay();
    } else {
      console.log('⏳ [INVESTIGATION] Waiting for canplay event (readyState < 2)');
      // Wait for canplay event, then start delay
      const canPlayHandler = () => {
        console.log('🎉 [INVESTIGATION] canplay event fired!');
        handleCanPlay();
      };
      audio.addEventListener('canplay', canPlayHandler, { once: true });
      
      return () => {
        console.log('🧹 [INVESTIGATION] Cleaning up autoplay effect (canplay listener)');
        audio.removeEventListener('canplay', canPlayHandler);
        if (autoplayTimeoutRef.current) {
          console.log('🧹 [INVESTIGATION] Clearing autoplay timeout on cleanup');
          clearTimeout(autoplayTimeoutRef.current);
          autoplayTimeoutRef.current = null;
        }
      };
    }
    
    return () => {
      console.log('🧹 [INVESTIGATION] Cleaning up autoplay effect (immediate cleanup)');
      if (autoplayTimeoutRef.current) {
        console.log('🧹 [INVESTIGATION] Clearing autoplay timeout on cleanup');
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
      }
    };
    */
  }, [audioUrl]); // Only depend on audioUrl, not play()
  
  // Cleanup autoplay timeout on unmount
  useEffect(() => {
    return () => {
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
      }
    };
  }, []);
  
  // Stop audio when navigating away from the game
  useEffect(() => {
    const isInGame = location.pathname.startsWith('/play/');
    
    if (!isInGame && (isPlaying || audioUrl)) {
      console.log('🛑 Left game area - stopping audio');
      
      // Stop audio directly without calling pause() to avoid dependency issues
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
      
      setAudioUrlState(null);
      setCurrentTime(0);
    }
  }, [location.pathname, isPlaying, audioUrl]);
  
  const contextValue: AudioPlayerContextType = {
    audioRef,
    isPlaying,
    audioError,
    currentTime,
    duration,
    audioUrl,
    play,
    pause,
    toggle,
    setAudioUrl,
    seek,
  };
  
  // Monitor audio URL state changes
  useEffect(() => {
    console.log('🔄 [INVESTIGATION] audioUrl STATE changed in context');
    console.log('   New audioUrl:', audioUrl);
    console.log('   Timestamp:', new Date().toISOString());
  }, [audioUrl]);
  
  // Monitor actual audio element src attribute
  useEffect(() => {
    if (!audioRef.current) return;
    
    const audio = audioRef.current;
    console.log('📼 [INVESTIGATION] Audio element src updated via React');
    console.log('   audio.src:', audio.src);
    console.log('   audioUrl state:', audioUrl);
    console.log('   Match:', audio.src === audioUrl || audio.src.endsWith(audioUrl || ''));
  }, [audioUrl]);
  
  return (
    <AudioPlayerContext.Provider value={contextValue}>
      {children}
      {/* Render audio element at top level so it persists across view changes */}
      <audio
        ref={audioRef}
        src={audioUrl || undefined}
        preload="auto"
        style={{ display: 'none' }}
      />
    </AudioPlayerContext.Provider>
  );
};

// Hook to use the audio player context
export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};

